import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import PdfSignaturePlacer from "./PdfSignaturePlacer";

interface SignatureDoc {
  id: number;
  title: string;
  file_type?: string;
  signature_mode?: string | null;
  requires_initials?: boolean;
}

type SignatureTab = "draw" | "type" | "upload";
type Phase = "read" | "capture" | "capture_initials" | "place";

const FONTS = [
  { name: "Dancing Script", family: "'Dancing Script', cursive" },
  { name: "Great Vibes", family: "'Great Vibes', cursive" },
  { name: "Pacifico", family: "'Pacifico', cursive" },
  { name: "Satisfy", family: "'Satisfy', cursive" },
];

function textToDataUrl(text: string, fontFamily: string): string {
  const canvas = document.createElement("canvas");
  canvas.width = 400;
  canvas.height = 160;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#000";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  let fontSize = 48;
  ctx.font = `${fontSize}px ${fontFamily}`;
  while (ctx.measureText(text).width > 370 && fontSize > 16) {
    fontSize -= 2;
    ctx.font = `${fontSize}px ${fontFamily}`;
  }
  ctx.fillText(text, 200, 80);
  return canvas.toDataURL("image/png");
}

export default function SignatureModal({
  doc,
  onClose,
  onSubmit,
}: {
  doc: SignatureDoc;
  onClose: (signed: boolean) => void;
  /** Sign something other than an uploaded document (e.g. a job description
   *  kept in PME People): called with the captured signature instead of
   *  posting to /api/documents/{id}/sign. */
  onSubmit?: (signatureData: string, signatureType: string) => Promise<void>;
}) {
  const { t } = useTranslation();

  const isOnDocument =
    doc.signature_mode === "on_document" &&
    (doc.file_type?.toLowerCase() === "pdf" || !doc.file_type);

  const [phase, setPhase] = useState<Phase>(isOnDocument ? "read" : "capture");
  const [tab, setTab] = useState<SignatureTab>("draw");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [capturedSig, setCapturedSig] = useState<{
    dataUrl: string;
    type: string;
  } | null>(null);
  const [capturedInitials, setCapturedInitials] = useState<string | null>(null);

  const needsInitials = isOnDocument && !!doc.requires_initials;

  // Draw state
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [hasStrokes, setHasStrokes] = useState(false);

  // Type state
  const [typedText, setTypedText] = useState("");
  const [selectedFont, setSelectedFont] = useState(0);

  // Upload state
  const [uploadedDataUrl, setUploadedDataUrl] = useState<string | null>(null);

  const getCtx = () => canvasRef.current?.getContext("2d") ?? null;

  const startDraw = (x: number, y: number) => {
    const ctx = getCtx();
    if (!ctx) return;
    setDrawing(true);
    setHasStrokes(true);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (x: number, y: number) => {
    if (!drawing) return;
    const ctx = getCtx();
    if (!ctx) return;
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000";
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDraw = () => setDrawing(false);

  const canvasCoords = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const scaleX = 400 / rect.width;
    const scaleY = 160 / rect.height;
    return [
      (e.clientX - rect.left) * scaleX,
      (e.clientY - rect.top) * scaleY,
    ] as const;
  };

  const touchCoords = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const tc = e.touches[0];
    const scaleX = 400 / rect.width;
    const scaleY = 160 / rect.height;
    return [
      (tc.clientX - rect.left) * scaleX,
      (tc.clientY - rect.top) * scaleY,
    ] as const;
  };

  const clearCanvas = () => {
    const ctx = getCtx();
    if (!ctx || !canvasRef.current) return;
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasStrokes(false);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setUploadedDataUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const getSignatureData = useCallback((): string | null => {
    if (tab === "draw") {
      if (!canvasRef.current || !hasStrokes) return null;
      return canvasRef.current.toDataURL("image/png");
    }
    if (tab === "type") {
      if (!typedText.trim()) return null;
      return textToDataUrl(typedText.trim(), FONTS[selectedFont].family);
    }
    if (tab === "upload") {
      return uploadedDataUrl;
    }
    return null;
  }, [tab, hasStrokes, typedText, selectedFont, uploadedDataUrl]);

  const canSubmit =
    (tab === "draw" && hasStrokes) ||
    (tab === "type" && typedText.trim().length > 0) ||
    (tab === "upload" && uploadedDataUrl !== null);

  const submitDirect = async (sigData: string, sigType: string) => {
    setSubmitting(true);
    setError(null);
    try {
      if (onSubmit) {
        await onSubmit(sigData, sigType);
      } else {
        await api.post(`/api/documents/${doc.id}/sign`, {
          signature_data: sigData,
          signature_type: sigType,
          signature_mode: doc.signature_mode || "confirm_receipt",
        });
      }
      onClose(true);
    } catch (err) {
      setError((err as Error).message || "Signing failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitWithPlacement = async (
    placement: { page: number; x: number; y: number; width: number; height: number },
  ) => {
    if (!capturedSig) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/api/documents/${doc.id}/sign`, {
        signature_data: capturedSig.dataUrl,
        signature_type: capturedSig.type,
        signature_mode: "on_document",
        page_number: placement.page,
        x_position: placement.x,
        y_position: placement.y,
        sig_width: placement.width,
        sig_height: placement.height,
      });
      onClose(true);
    } catch (err) {
      setError((err as Error).message || "Signing failed");
      setPhase("capture");
    } finally {
      setSubmitting(false);
    }
  };

  const submitMultiPlacement = async (
    allPlacements: { page: number; x: number; y: number; width: number; height: number; type?: string }[],
  ) => {
    if (!capturedSig) return;
    setSubmitting(true);
    setError(null);
    try {
      const sigP = allPlacements.find((p) => p.type === "signature") ?? allPlacements[0];
      await api.post(`/api/documents/${doc.id}/sign`, {
        signature_data: capturedSig.dataUrl,
        signature_type: capturedSig.type,
        signature_mode: "on_document",
        page_number: sigP.page,
        x_position: sigP.x,
        y_position: sigP.y,
        sig_width: sigP.width,
        sig_height: sigP.height,
        placements: allPlacements,
        initials_data: capturedInitials ?? undefined,
      });
      onClose(true);
    } catch (err) {
      setError((err as Error).message || "Signing failed");
      setPhase("capture");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSign = () => {
    const sigData = getSignatureData();
    if (!sigData || submitting) return;
    const sigType =
      tab === "draw" ? "drawn" : tab === "type" ? "typed" : "uploaded";

    if (isOnDocument) {
      setCapturedSig({ dataUrl: sigData, type: sigType });
      if (needsInitials && !capturedInitials) {
        setPhase("capture_initials");
      } else {
        setPhase("place");
      }
    } else {
      void submitDirect(sigData, sigType);
    }
  };

  // Phase: Read document first (on_document mode only)
  if (phase === "read") {
    return (
      <DocumentReadPhase
        doc={doc}
        onProceed={() => setPhase("capture")}
        onCancel={() => onClose(false)}
      />
    );
  }

  // Phase: Capture initials (when document requires them)
  if (phase === "capture_initials" && capturedSig) {
    return (
      <InitialsCapturePhase
        onCapture={(dataUrl) => {
          setCapturedInitials(dataUrl);
          setPhase("place");
        }}
        onCancel={() => onClose(false)}
        onBack={() => setPhase("capture")}
      />
    );
  }

  // Phase: Place signature on PDF
  if (phase === "place" && capturedSig) {
    if (submitting) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative z-10 rounded-2xl bg-white p-8 text-center shadow-xl">
            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-pme-blue" />
            <p className="text-sm text-gray-600">
              {t("documents.applyingSignature", "Applying signature to document...")}
            </p>
          </div>
        </div>
      );
    }
    return (
      <PdfSignaturePlacer
        documentId={doc.id}
        signatureDataUrl={capturedSig.dataUrl}
        initialsDataUrl={capturedInitials ?? undefined}
        requiresInitials={needsInitials}
        onConfirm={(placement) => void submitWithPlacement(placement)}
        onConfirmMulti={(pls) => void submitMultiPlacement(pls)}
        onCancel={() => {
          setPhase("capture");
          setCapturedSig(null);
        }}
      />
    );
  }

  // Phase: Capture signature
  const tabs: { key: SignatureTab; label: string }[] = [
    { key: "draw", label: t("documents.signDraw", "Draw") },
    { key: "type", label: t("documents.signType", "Type") },
    { key: "upload", label: t("documents.signUpload", "Upload") },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/50" onClick={() => onClose(false)} />
      <div className="relative z-10 w-full max-w-md rounded-t-2xl bg-white p-6 shadow-xl sm:rounded-2xl">
        <h2 className="mb-1 text-lg font-semibold text-gray-900">
          {t("documents.signDocument", "Sign Document")}
        </h2>
        <p className="mb-4 text-sm text-gray-500">{doc.title}</p>

        {isOnDocument && (
          <p className="mb-3 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-700">
            {t(
              "documents.onDocumentHint",
              "After capturing your signature, you will place it on the document.",
            )}
          </p>
        )}

        {/* Tab bar */}
        <div className="mb-4 flex gap-1 rounded-lg bg-gray-100 p-1">
          {tabs.map((tb) => (
            <button
              key={tb.key}
              type="button"
              onClick={() => setTab(tb.key)}
              className={[
                "flex-1 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                tab === tb.key
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700",
              ].join(" ")}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {/* Draw tab */}
        {tab === "draw" && (
          <>
            <div className="mb-2 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
              <canvas
                ref={canvasRef}
                width={400}
                height={160}
                className="w-full cursor-crosshair touch-none"
                onMouseDown={(e) => {
                  const [x, y] = canvasCoords(e);
                  startDraw(x, y);
                }}
                onMouseMove={(e) => {
                  const [x, y] = canvasCoords(e);
                  draw(x, y);
                }}
                onMouseUp={stopDraw}
                onMouseLeave={stopDraw}
                onTouchStart={(e) => {
                  e.preventDefault();
                  const [x, y] = touchCoords(e);
                  startDraw(x, y);
                }}
                onTouchMove={(e) => {
                  e.preventDefault();
                  const [x, y] = touchCoords(e);
                  draw(x, y);
                }}
                onTouchEnd={stopDraw}
              />
            </div>
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs text-gray-400">
                {t("documents.drawHint", "Draw your signature above")}
              </p>
              <button
                type="button"
                onClick={clearCanvas}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                {t("documents.clear", "Clear")}
              </button>
            </div>
          </>
        )}

        {/* Type tab */}
        {tab === "type" && (
          <>
            <input
              type="text"
              value={typedText}
              onChange={(e) => setTypedText(e.target.value)}
              placeholder={t(
                "documents.typeNamePlaceholder",
                "Type your full name",
              )}
              className="input mb-3 w-full"
              autoFocus
            />
            <div className="mb-3 grid grid-cols-2 gap-2">
              {FONTS.map((f, i) => (
                <button
                  key={f.name}
                  type="button"
                  onClick={() => setSelectedFont(i)}
                  className={[
                    "rounded-lg border-2 px-3 py-3 text-center text-lg transition-colors",
                    selectedFont === i
                      ? "border-pme-blue bg-blue-50"
                      : "border-gray-200 hover:border-gray-300",
                  ].join(" ")}
                  style={{ fontFamily: f.family }}
                >
                  {typedText.trim() || "Your Name"}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Upload tab */}
        {tab === "upload" && (
          <div className="mb-3">
            {uploadedDataUrl ? (
              <div className="relative rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-4">
                <img
                  src={uploadedDataUrl}
                  alt="Signature"
                  className="mx-auto max-h-32 object-contain"
                />
                <button
                  type="button"
                  onClick={() => setUploadedDataUrl(null)}
                  className="absolute right-2 top-2 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600"
                >
                  {t("documents.remove", "Remove")}
                </button>
              </div>
            ) : (
              <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-sm text-gray-500">
                  {t("documents.uploadSigHint", "Upload a signature image")}
                </span>
                <span className="text-xs text-gray-400">PNG, JPG</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>
        )}

        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSign}
            disabled={!canSubmit || submitting}
            className="flex-1 rounded-lg bg-pme-blue py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {submitting
              ? t("documents.signing", "Signing...")
              : isOnDocument
                ? t("documents.nextPlaceOnDoc", "Next: Place on Document")
                : t("documents.sign", "Sign")}
          </button>
          <button
            type="button"
            onClick={() => onClose(false)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
          >
            {t("common.cancel", "Cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Document Read Phase (scroll-to-bottom requirement) ---------- */

function DocumentReadPhase({
  doc,
  onProceed,
  onCancel,
}: {
  doc: SignatureDoc;
  onProceed: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfDoc, setPdfDoc] = useState<import("pdfjs-dist").PDFDocumentProxy | null>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasCheckedRead, setHasCheckedRead] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Shared pdf.js setup (Map-upsert polyfill + polyfilled worker port).
        const { pdfjsLib } = await import("../utils/pdfjs");

        const resp = await fetch(
          `/api/documents/${doc.id}/download?disposition=inline`,
          { credentials: "include" },
        );
        if (!resp.ok) throw new Error("Failed to fetch document");
        const ct = resp.headers.get("content-type") ?? "";
        let pdfData: ArrayBuffer;
        if (ct.includes("application/json")) {
          const data = (await resp.json()) as { url: string };
          const pdfResp = await fetch(data.url);
          pdfData = await pdfResp.arrayBuffer();
        } else {
          pdfData = await resp.arrayBuffer();
        }
        if (cancelled) return;
        const pDoc = await pdfjsLib.getDocument({ data: pdfData }).promise;
        if (cancelled) return;
        setPdfDoc(pDoc);

        // Single-page: immediately mark as scrolled
        if (pDoc.numPages <= 1) {
          setHasScrolledToBottom(true);
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [doc.id]);

  const handleScroll = useCallback(() => {
    if (hasScrolledToBottom) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const threshold = 50;
    const atBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
    if (atBottom) {
      setHasScrolledToBottom(true);
    }
  }, [hasScrolledToBottom]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    // Check immediately in case content fits without scrolling
    handleScroll();
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll, pdfDoc]);

  const canProceed = hasScrolledToBottom && hasCheckedRead;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative z-10 flex max-h-[92vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {t("documents.reviewDocument", "Review Document")}
          </h2>
          <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        <p className="border-b bg-gray-50 px-6 py-2 text-xs text-gray-600">
          {doc.title}
        </p>

        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto bg-gray-200 p-4"
        >
          {loading && (
            <div className="flex h-64 items-center justify-center">
              <p className="text-sm text-gray-500">{t("common.loading", "Loading...")}</p>
            </div>
          )}
          {error && (
            <div className="flex h-64 items-center justify-center">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          {pdfDoc && (
            <div ref={contentRef} className="space-y-4">
              {Array.from({ length: pdfDoc.numPages }, (_, i) => (
                <ReadOnlyPdfPage key={i} doc={pdfDoc} pageNum={i + 1} />
              ))}
            </div>
          )}
        </div>

        {/* Scroll hint */}
        {!hasScrolledToBottom && !loading && pdfDoc && (
          <div className="border-t bg-amber-50 px-6 py-2 text-center text-xs text-amber-700">
            {t("documents.scrollToBottom", "Please scroll to the bottom to continue")}
          </div>
        )}

        <div className="border-t px-6 py-4">
          <label
            className={[
              "mb-3 flex items-center gap-2 text-sm",
              hasScrolledToBottom ? "text-gray-700" : "text-gray-400",
            ].join(" ")}
          >
            <input
              type="checkbox"
              checked={hasCheckedRead}
              onChange={(e) => setHasCheckedRead(e.target.checked)}
              disabled={!hasScrolledToBottom}
              className="h-4 w-4 rounded border-gray-300 accent-pme-blue disabled:opacity-40"
            />
            {t("documents.haveReadDocument", "I have read this document")}
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onProceed}
              disabled={!canProceed}
              className="flex-1 rounded-lg bg-pme-blue py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {t("documents.proceedToSign", "Proceed to Sign")}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
            >
              {t("common.cancel", "Cancel")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InitialsCapturePhase({
  onCapture,
  onCancel,
  onBack,
}: {
  onCapture: (dataUrl: string) => void;
  onCancel: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [hasStrokes, setHasStrokes] = useState(false);

  const getCtx = () => canvasRef.current?.getContext("2d") ?? null;

  const startDraw = (x: number, y: number) => {
    const ctx = getCtx();
    if (!ctx) return;
    setDrawing(true);
    setHasStrokes(true);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (x: number, y: number) => {
    if (!drawing) return;
    const ctx = getCtx();
    if (!ctx) return;
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000";
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDraw = () => setDrawing(false);

  const coords = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const scaleX = 200 / rect.width;
    const scaleY = 100 / rect.height;
    return [(e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY] as const;
  };

  const touchCoords = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const tc = e.touches[0];
    const scaleX = 200 / rect.width;
    const scaleY = 100 / rect.height;
    return [(tc.clientX - rect.left) * scaleX, (tc.clientY - rect.top) * scaleY] as const;
  };

  const clearCanvas = () => {
    const ctx = getCtx();
    if (!ctx || !canvasRef.current) return;
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasStrokes(false);
  };

  const handleConfirm = () => {
    if (!canvasRef.current || !hasStrokes) return;
    onCapture(canvasRef.current.toDataURL("image/png"));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative z-10 w-full max-w-sm rounded-t-2xl bg-white p-6 shadow-xl sm:rounded-2xl">
        <h2 className="mb-1 text-lg font-semibold text-gray-900">
          {t("documents.drawInitials", "Draw Your Initials")}
        </h2>
        <p className="mb-4 text-sm text-gray-500">
          {t("documents.initialsHint", "Draw your initials below. These will be placed on the document.")}
        </p>

        <div className="mb-2 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
          <canvas
            ref={canvasRef}
            width={200}
            height={100}
            className="w-full cursor-crosshair touch-none"
            onMouseDown={(e) => { const [x, y] = coords(e); startDraw(x, y); }}
            onMouseMove={(e) => { const [x, y] = coords(e); draw(x, y); }}
            onMouseUp={stopDraw}
            onMouseLeave={stopDraw}
            onTouchStart={(e) => { e.preventDefault(); const [x, y] = touchCoords(e); startDraw(x, y); }}
            onTouchMove={(e) => { e.preventDefault(); const [x, y] = touchCoords(e); draw(x, y); }}
            onTouchEnd={stopDraw}
          />
        </div>
        <div className="mb-4 flex items-center justify-between">
          <p className="text-xs text-gray-400">
            {t("documents.drawInitialsHint", "Draw your initials above")}
          </p>
          <button type="button" onClick={clearCanvas} className="text-xs text-gray-500 hover:text-gray-700">
            {t("documents.clear", "Clear")}
          </button>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!hasStrokes}
            className="flex-1 rounded-lg bg-pme-blue py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {t("documents.nextPlaceOnDoc", "Next: Place on Document")}
          </button>
          <button type="button" onClick={onBack} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">
            {t("common.back", "Back")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReadOnlyPdfPage({
  doc,
  pageNum,
}: {
  doc: import("pdfjs-dist").PDFDocumentProxy;
  pageNum: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const page = await doc.getPage(pageNum);
      if (cancelled) return;
      const containerWidth = wrapperRef.current?.parentElement?.clientWidth ?? 600;
      const maxWidth = Math.min(containerWidth - 32, 700);
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = maxWidth / baseViewport.width;
      const viewport = page.getViewport({ scale });

      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      await page.render({ canvasContext: ctx, viewport, canvas }).promise;
    })();
    return () => { cancelled = true; };
  }, [doc, pageNum]);

  return (
    <div ref={wrapperRef} className="mx-auto">
      <div className="mb-1 text-right text-xs text-gray-500">{pageNum}</div>
      <canvas ref={canvasRef} className="block rounded shadow-lg" />
    </div>
  );
}
